/**
 * Apply an `ImportPlan` to PlaneMAX.
 *
 * Writes go through the same API functions the UI uses (`createHangar`,
 * `createUnit`, `createAircraft`, `createAllocation`, `createObstacle`) so
 * imported rows are indistinguishable from hand-authored ones and open cleanly
 * in the building editor.
 *
 * Ordering is buildings → aircraft → obstacles, because items need their host
 * unit ids. Every write is wrapped: a failure marks that object's link `error`
 * and the run continues, so one bad object cannot abandon a 200-object layout.
 * Re-running resumes — links are upserted on their natural key.
 */

import { createAircraft, updateAircraft } from '@/lib/api/aircraft'
import { createAllocation, updateAllocation } from '@/lib/api/allocations'
import { createHangar, fetchBuildingWithAllUnits, updateHangar } from '@/lib/api/hangars'
import { createObstacle, updateObstacle } from '@/lib/api/obstacles'
import { createUnit, updateUnit } from '@/lib/api/units'
import { generateAreaCode } from '@/lib/utils/areaCodeGenerator'
import {
  getSingleUnitTemplate,
  type SingleUnitParams,
} from '@/lib/templates'
import {
  markLinkSourceDeleted,
  upsertImportLink,
  type UpsertLinkInput,
} from '@/lib/api/hangarmaxImport'
import type {
  ImportCounts,
  ImportPlan,
  PlannedAircraft,
  PlannedBuilding,
  PlannedObstacle,
  PlannedSkip,
} from './types'

export interface ExecuteProgress {
  phase: 'buildings' | 'aircraft' | 'obstacles' | 'links' | 'done'
  completed: number
  total: number
  label: string
}

export interface ExecuteResult {
  counts: ImportCounts
  errors: Array<{ label: string; message: string }>
  /** Objects preserved but not imported, for the final report. */
  unmatched: Array<{ label: string; reason: string }>
}

export interface ExecuteOptions {
  organizationId: string
  locationId: string
  sourceId: string
  plan: ImportPlan
  onProgress?: (progress: ExecuteProgress) => void
}

/** Resolved ids of a building written in this run. */
interface WrittenBuilding {
  areaId: string
  unitIds: string[]
  widthFt: number
}

export async function executeImportPlan(options: ExecuteOptions): Promise<ExecuteResult> {
  const { organizationId, locationId, sourceId, plan, onProgress } = options

  const errors: ExecuteResult['errors'] = []
  const written = new Map<string, WrittenBuilding>()
  const counts: ImportCounts = {
    created: 0,
    updated: 0,
    unchanged: 0,
    unmatched: 0,
    sourceDeleted: 0,
  }

  const total =
    plan.buildings.length +
    plan.aircraft.length +
    plan.obstacles.length +
    plan.skipped.length +
    plan.sourceDeleted.length
  let completed = 0

  const tick = (phase: ExecuteProgress['phase'], label: string) => {
    completed++
    onProgress?.({ phase, completed, total, label })
  }

  // --------------------------------------------------------------------------
  // Buildings
  // --------------------------------------------------------------------------
  for (const building of plan.buildings) {
    try {
      const result = await writeBuilding({ building, organizationId, locationId })
      written.set(building.sourceObjectId, result)

      if (building.op === 'create') counts.created++
      else if (building.op === 'update') counts.updated++
      else counts.unchanged++

      await upsertImportLink(
        linkPayload({
          organizationId,
          sourceId,
          object: building,
          sourceObjectType: 'building',
          targetTable: 'areas',
          targetId: result.areaId,
          targetRefs: { area_id: result.areaId, unit_ids: result.unitIds },
          status: building.approximate ? 'approximate' : 'imported',
          statusDetail: building.fidelityNotes.join(' ') || null,
          matchInfo: { fidelity_notes: building.fidelityNotes },
        })
      )
    } catch (error) {
      await recordError({
        organizationId,
        sourceId,
        object: building,
        sourceObjectType: 'building',
        error,
        errors,
      })
    }
    tick('buildings', building.label)
  }

  // --------------------------------------------------------------------------
  // Aircraft
  // --------------------------------------------------------------------------
  for (const aircraft of plan.aircraft) {
    try {
      const host = aircraft.host ? written.get(aircraft.host.buildingSourceId) : undefined
      const unitId = host?.unitIds[aircraft.host?.unitIndex ?? 0] ?? null
      const areaId = host?.areaId ?? null

      const ids = await writeAircraft({
        aircraft,
        organizationId,
        locationId,
        areaId,
        unitId,
      })

      if (aircraft.op === 'create') counts.created++
      else if (aircraft.op === 'update') counts.updated++
      else counts.unchanged++

      await upsertImportLink(
        linkPayload({
          organizationId,
          sourceId,
          object: aircraft,
          sourceObjectType: 'item',
          targetTable: 'aircraft',
          targetId: ids.aircraftId,
          targetRefs: { aircraft_id: ids.aircraftId, allocation_id: ids.allocationId },
          status: aircraft.match.aircraftTypeId ? 'imported' : 'approximate',
          statusDetail: aircraft.fidelityNotes.join(' ') || null,
          matchInfo: {
            method: aircraft.match.method,
            aircraft_type_id: aircraft.match.aircraftTypeId,
            confidence: aircraft.match.confidence,
            fidelity_notes: aircraft.fidelityNotes,
          },
        })
      )
    } catch (error) {
      await recordError({
        organizationId,
        sourceId,
        object: aircraft,
        sourceObjectType: 'item',
        error,
        errors,
      })
    }
    tick('aircraft', aircraft.label)
  }

  // --------------------------------------------------------------------------
  // Obstacles
  // --------------------------------------------------------------------------
  for (const obstacle of plan.obstacles) {
    try {
      const host = written.get(obstacle.host.buildingSourceId)
      const unitId = host?.unitIds[obstacle.host.unitIndex]
      if (!unitId) {
        throw new Error('Host unit was not created, so this obstacle has nowhere to live.')
      }

      const obstacleId = await writeObstacle({
        obstacle,
        organizationId,
        locationId,
        unitId,
      })

      if (obstacle.op === 'create') counts.created++
      else if (obstacle.op === 'update') counts.updated++
      else counts.unchanged++

      await upsertImportLink(
        linkPayload({
          organizationId,
          sourceId,
          object: obstacle,
          sourceObjectType: 'item',
          targetTable: 'obstacles',
          targetId: obstacleId,
          targetRefs: { obstacle_id: obstacleId, unit_id: unitId },
          status: 'imported',
          statusDetail: obstacle.fidelityNotes.join(' ') || null,
          matchInfo: { fidelity_notes: obstacle.fidelityNotes },
        })
      )
    } catch (error) {
      await recordError({
        organizationId,
        sourceId,
        object: obstacle,
        sourceObjectType: 'item',
        error,
        errors,
      })
    }
    tick('obstacles', obstacle.label)
  }

  // --------------------------------------------------------------------------
  // Preserved-but-not-imported objects, and source deletions
  // --------------------------------------------------------------------------
  const unmatched: ExecuteResult['unmatched'] = []

  for (const skip of plan.skipped) {
    unmatched.push({ label: skip.label, reason: skip.reason })
    counts.unmatched++
    try {
      await upsertImportLink(
        linkPayload({
          organizationId,
          sourceId,
          object: skip,
          sourceObjectType: 'item',
          targetTable: null,
          targetId: null,
          targetRefs: null,
          status: 'unmatched',
          statusDetail: skip.reason,
          matchInfo: null,
        })
      )
    } catch (error) {
      errors.push({ label: skip.label, message: messageOf(error) })
    }
    tick('links', skip.label)
  }

  for (const deleted of plan.sourceDeleted) {
    counts.sourceDeleted++
    try {
      await markLinkSourceDeleted(deleted.linkId)
    } catch (error) {
      errors.push({ label: deleted.label, message: messageOf(error) })
    }
    tick('links', deleted.label)
  }

  onProgress?.({ phase: 'done', completed: total, total, label: 'Finished' })

  return { counts, errors, unmatched }
}

// ----------------------------------------------------------------------------
// Writers
// ----------------------------------------------------------------------------

function templateParamsFor(
  building: PlannedBuilding,
  areaCode: string
): SingleUnitParams {
  return {
    name: building.area.name,
    code: areaCode,
    width_ft: building.area.widthFt,
    depth_ft: building.area.depthFt,
    height_ft: building.area.heightFt,
    wall_thickness_ft: 0.5,
    doors: building.area.doors,
    floor_color: building.area.floorColor,
    wall_color: building.area.wallColor,
    latitude: building.area.latitude,
    longitude: building.area.longitude,
    bearing_deg: building.area.rotationDeg,
  }
}

async function writeBuilding(params: {
  building: PlannedBuilding
  organizationId: string
  locationId: string
}): Promise<WrittenBuilding> {
  const { building, organizationId, locationId } = params
  const template = getSingleUnitTemplate()

  // No-op with a known target: nothing to write, just report the existing ids.
  if (building.op === 'noop' && building.targetAreaId) {
    return {
      areaId: building.targetAreaId,
      unitIds: building.targetUnitIds,
      widthFt: building.area.widthFt,
    }
  }

  if (building.op === 'update' && building.targetAreaId) {
    const areaId = building.targetAreaId
    const existing = await fetchBuildingWithAllUnits(areaId)
    const areaCode = existing.building.area_code
    const templateParams = templateParamsFor(building, areaCode)
    const areaConfig = template.generateBuilding(templateParams)
    const unitConfigs = template.generateUnits(areaConfig, templateParams)

    await updateHangar(areaId, {
      name: areaConfig.name,
      exterior_width: areaConfig.exterior_width,
      exterior_depth: areaConfig.exterior_depth,
      exterior_height: areaConfig.exterior_height,
      latitude: building.area.latitude,
      longitude: building.area.longitude,
      rotation: building.area.rotationDeg,
      door_width: areaConfig.door_width,
      door_height: areaConfig.door_height,
      type_config: areaConfig.type_config,
      settings: areaConfig.settings,
    })

    // Reconcile units by index. Surplus units are deliberately LEFT ALONE:
    // they may hold leases or allocations the source layout knows nothing
    // about, and a source-wins update must not destroy PlaneMAX-side tenancy.
    const unitIds: string[] = []
    for (let i = 0; i < unitConfigs.length; i++) {
      const config = unitConfigs[i]
      const existingUnit = existing.units[i]
      if (existingUnit) {
        await updateUnit(existingUnit.id, {
          interior_width: config.interior_width,
          interior_depth: config.interior_depth,
          interior_height: config.interior_height,
          door_width: config.door_width,
          door_height: config.door_height,
          door_type: config.door_type,
        })
        unitIds.push(existingUnit.id)
      } else {
        const created = await createUnit({
          area_id: areaId,
          unit_code: config.code,
          name: `${areaConfig.name} Unit`,
          type: config.type,
          capacity_strategy: config.capacity_strategy,
          interior_width: config.interior_width,
          interior_depth: config.interior_depth,
          interior_height: config.interior_height,
          door_width: config.door_width,
          door_height: config.door_height,
          door_type: config.door_type,
          offset_x: config.offset_x,
          offset_y: config.offset_y,
          unit_rotation: config.unit_rotation,
          is_leasable: true,
          status: 'available',
        })
        unitIds.push(created.id)
      }
    }

    return { areaId, unitIds, widthFt: building.area.widthFt }
  }

  // Create (also covers "link exists but the area was deleted").
  const areaCode = await generateAreaCode(organizationId, 'single_unit_hangar')
  const templateParams = templateParamsFor(building, areaCode)
  const areaConfig = template.generateBuilding(templateParams)
  const unitConfigs = template.generateUnits(areaConfig, templateParams)

  const area = await createHangar({
    organization_id: organizationId,
    location_id: locationId,
    name: areaConfig.name,
    area_code: areaCode,
    type: areaConfig.type,
    exterior_width: areaConfig.exterior_width,
    exterior_depth: areaConfig.exterior_depth,
    exterior_height: areaConfig.exterior_height,
    latitude: building.area.latitude,
    longitude: building.area.longitude,
    rotation: building.area.rotationDeg,
    door_width: areaConfig.door_width,
    door_height: areaConfig.door_height,
    status: 'active',
    type_config: areaConfig.type_config,
    settings: areaConfig.settings,
  })

  const unitIds: string[] = []
  for (const config of unitConfigs) {
    const created = await createUnit({
      area_id: area.id,
      unit_code: config.code,
      name: `${areaConfig.name} Unit`,
      type: config.type,
      capacity_strategy: config.capacity_strategy,
      interior_width: config.interior_width,
      interior_depth: config.interior_depth,
      interior_height: config.interior_height,
      door_width: config.door_width,
      door_height: config.door_height,
      door_type: config.door_type,
      offset_x: config.offset_x,
      offset_y: config.offset_y,
      unit_rotation: config.unit_rotation,
      is_leasable: true,
      status: 'available',
    })
    unitIds.push(created.id)
  }

  return { areaId: area.id, unitIds, widthFt: building.area.widthFt }
}

async function writeAircraft(params: {
  aircraft: PlannedAircraft
  organizationId: string
  locationId: string
  areaId: string | null
  unitId: string | null
}): Promise<{ aircraftId: string; allocationId: string | null }> {
  const { aircraft, organizationId, locationId, areaId, unitId } = params

  const allocationFields = {
    location_id: locationId,
    area_id: areaId,
    unit_id: unitId,
    position_type: (unitId ? 'hangar' : 'ramp') as 'hangar' | 'ramp',
    position_x: aircraft.position[0],
    position_y: aircraft.position[1],
    position_z: aircraft.position[2],
    rotation: aircraft.rotationDeg,
  }

  if (aircraft.op === 'noop' && aircraft.targetAircraftId) {
    return {
      aircraftId: aircraft.targetAircraftId,
      allocationId: aircraft.targetAllocationId,
    }
  }

  if (aircraft.op === 'update' && aircraft.targetAircraftId) {
    // Identity stays user-owned: the tail number, owner and client links are
    // never overwritten by the source. Only geometry and type flow through.
    await updateAircraft(aircraft.targetAircraftId, {
      aircraft_type_id: aircraft.match.aircraftTypeId,
      wingspan: aircraft.wingspanFt,
      length: aircraft.lengthFt,
      height: aircraft.heightFt,
    })

    if (aircraft.targetAllocationId) {
      await updateAllocation(aircraft.targetAllocationId, allocationFields)
      return {
        aircraftId: aircraft.targetAircraftId,
        allocationId: aircraft.targetAllocationId,
      }
    }

    const allocation = await createAllocation(organizationId, {
      aircraft_id: aircraft.targetAircraftId,
      ...allocationFields,
      status: 'active',
      presence: 'present',
    })
    return { aircraftId: aircraft.targetAircraftId, allocationId: allocation.id }
  }

  const created = await createAircraft(organizationId, {
    tail_number: aircraft.tailNumber ?? `TEMP_${aircraft.sourceObjectId}`,
    aircraft_type_id: aircraft.match.aircraftTypeId,
    manufacturer: aircraft.manufacturer,
    model: aircraft.model,
    owner_client_id: null,
    operator_client_id: null,
    owner_name: null,
    length: aircraft.lengthFt,
    wingspan: aircraft.wingspanFt,
    height: aircraft.heightFt,
    tail_height: null,
    empty_weight: null,
    max_takeoff_weight: null,
    registration_country: null,
    serial_number: null,
    year_manufactured: null,
    annual_inspection_due: null,
    insurance_expiry: null,
    home_location_id: null,
    current_location_id: locationId,
    current_area_id: areaId,
    custom_model_file_path: null,
    custom_model_scale: null,
    status: 'active',
    notes: `Imported from HangarMax (${aircraft.label}).`,
  })

  const allocation = await createAllocation(organizationId, {
    aircraft_id: created.id,
    ...allocationFields,
    status: 'active',
    presence: 'present',
  })

  return { aircraftId: created.id, allocationId: allocation.id }
}

async function writeObstacle(params: {
  obstacle: PlannedObstacle
  organizationId: string
  locationId: string
  unitId: string
}): Promise<string> {
  const { obstacle, organizationId, locationId, unitId } = params

  const fields = {
    unit_id: unitId,
    name: obstacle.name,
    obstacle_type: obstacle.obstacleType,
    shape: obstacle.shape,
    position_x: obstacle.positionX,
    position_y: obstacle.positionY,
    rotation: obstacle.rotation,
    width: obstacle.widthFt,
    depth: obstacle.depthFt,
    radius: obstacle.radiusFt,
    height: obstacle.heightFt,
    color: obstacle.color,
  }

  if (obstacle.op === 'noop' && obstacle.targetObstacleId) {
    return obstacle.targetObstacleId
  }

  if (obstacle.op === 'update' && obstacle.targetObstacleId) {
    await updateObstacle(obstacle.targetObstacleId, fields)
    return obstacle.targetObstacleId
  }

  const created = await createObstacle({
    organization_id: organizationId,
    location_id: locationId,
    ...fields,
  })
  return created.id
}

// ----------------------------------------------------------------------------
// Link helpers
// ----------------------------------------------------------------------------

function linkPayload(params: {
  organizationId: string
  sourceId: string
  object: PlannedBuilding | PlannedAircraft | PlannedObstacle | PlannedSkip
  sourceObjectType: 'building' | 'item'
  targetTable: 'areas' | 'aircraft' | 'obstacles' | null
  targetId: string | null
  targetRefs: Record<string, unknown> | null
  status: UpsertLinkInput['status']
  statusDetail: string | null
  matchInfo: Record<string, unknown> | null
}): UpsertLinkInput {
  const { object } = params
  return {
    id: object.linkId,
    organizationId: params.organizationId,
    sourceId: params.sourceId,
    sourceObjectType: params.sourceObjectType,
    sourceObjectId: object.sourceObjectId,
    catalogId: 'catalogId' in object ? object.catalogId : null,
    targetTable: params.targetTable,
    targetId: params.targetId,
    targetRefs: params.targetRefs,
    status: params.status,
    statusDetail: params.statusDetail,
    matchInfo: params.matchInfo,
    rawSource: object.raw,
    sourceHash: object.sourceHash,
    firstImport: object.linkId === null,
  }
}

async function recordError(params: {
  organizationId: string
  sourceId: string
  object: PlannedBuilding | PlannedAircraft | PlannedObstacle
  sourceObjectType: 'building' | 'item'
  error: unknown
  errors: ExecuteResult['errors']
}): Promise<void> {
  const message = messageOf(params.error)
  params.errors.push({ label: params.object.label, message })

  try {
    await upsertImportLink(
      linkPayload({
        organizationId: params.organizationId,
        sourceId: params.sourceId,
        object: params.object,
        sourceObjectType: params.sourceObjectType,
        targetTable: null,
        targetId: null,
        targetRefs: null,
        status: 'error',
        statusDetail: message,
        matchInfo: null,
      })
    )
  } catch {
    // The link write failed too — the in-memory error list still reports it,
    // and the next run re-plans this object from scratch.
  }
}

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message
  return String(error)
}
